import React from 'react';

import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

import { AdminContext } from './AdminContext';
import { DatabaseContext } from '../database/DatabaseHandler';
import ScriptsTab from './ScriptsTab';
import StyleParamsEditor from './StyleParamsEditor';
import AddParamForm from './AddParamForm';
import ViewOptionsTab from './ViewOptionsTab';
import DxfExportButton from './DxfExportButton';
import NewCartButton from './NewCartButton';
import QuoteTab from './QuoteTab';
import PrestaGlobals from '../common/PrestaGlobals';

const DRAWER_WIDTH = 600;

/**
 * Configurator Admin panel (EA-437). Opens/closes via the AdminContext `panelOpen` flag set by
 * AdminModeToggle. M1 surfaces, read-only:
 *  - Szkriptek: the product's UCon script (EA-439) and the style script sent to Unity
 *    (SetStyleScript, editable as a temporary override), loadable from files,
 *  - the full parameter list including hidden parameters, which are marked (EA-447),
 * The AI designer has its own page (/ai-designer, see docs/admin/AI_DESIGNER.md); the header links there.
 * Editing, validation and persistence are added in later milestones.
 */
const AdminPanel: React.FC = () => {
    const admin = React.useContext(AdminContext);
    const data = React.useContext(DatabaseContext);
    const [tab, setTab] = React.useState(0);

    if (!admin.isAdmin) {
        return null;
    }

    const params = data.paramScripts ?? [];
    const hiddenCount = params.filter(p => p.typeDescriptor?.hidden).length;

    return (
        <Drawer
            anchor="right"
            open={admin.panelOpen}
            onClose={() => admin.setPanelOpen(false)}
            // Closed or on another tab, an unapplied edit stays (the tabs are hidden, not unmounted)
            ModalProps={{ keepMounted: true }}
            PaperProps={{ sx: { width: '50%', minWidth: DRAWER_WIDTH, maxWidth: '100vw' } }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2 }}>
                <Box>
                    <Typography variant="h6">Konfigurátor Admin</Typography>
                    <Typography variant="caption" color="text.secondary">
                        Termék #{PrestaGlobals.productId} · {PrestaGlobals.shopName} (bolt #{PrestaGlobals.shopId})
                    </Typography>
                </Box>
                <Button size="small" startIcon={<AutoAwesomeIcon />} href={`${PrestaGlobals.virtualUri}/ai-designer`}
                    sx={{ ml: 'auto', mr: 1 }}>
                    AI tervező
                </Button>
                <Box sx={{ mr: 1 }}><DxfExportButton /></Box>
                <Box sx={{ mr: 1 }}><NewCartButton /></Box>
                <IconButton aria-label="bezárás" onClick={() => admin.setPanelOpen(false)}
                    sx={{ color: 'text.secondary' }}>
                    <CloseIcon />
                </IconButton>
            </Box>
            <Divider />

            <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth">
                <Tab label="Szkriptek" />
                <Tab label={`Paraméterek (${params.length})`} />
                <Tab label="Nézet" />
                <Tab label="Árajánlat" />
            </Tabs>

            <Box sx={{ p: 2, overflow: 'auto', flex: 1, minHeight: 0 }}>
                <Box sx={{ display: tab === 0 ? 'block' : 'none', height: '100%' }}><ScriptsTab /></Box>

                <Box sx={{ display: tab === 1 ? 'block' : 'none' }}>
                    <Box>
                        {hiddenCount > 0 && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                {hiddenCount} rejtett paraméter is szerkeszthető admin módban.
                            </Typography>
                        )}
                        <AddParamForm />
                        <StyleParamsEditor />
                    </Box>
                </Box>

                <Box sx={{ display: tab === 2 ? 'block' : 'none' }}><ViewOptionsTab /></Box>

                {/* mounted on open: it prefills from the cart's saved quote then */}
                {tab === 3 && <QuoteTab />}
            </Box>
        </Drawer>
    );
};

export default AdminPanel;
